import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const hotMovies = movies.filter((m) => m.isHot).slice(0, 5);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning],
  );

  const next = useCallback(() => {
    goTo((currentIndex + 1) % hotMovies.length);
  }, [currentIndex, hotMovies.length, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  if (hotMovies.length === 0) return null;

  const movie = hotMovies[currentIndex];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {hotMovies.map((m, idx) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={m.image}
            alt={m.title}
            className="w-full h-full object-cover object-top"
            title={`${m.title} - ${m.subtitle}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

      <div className="absolute inset-0 flex items-end pb-12 md:pb-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div
              key={movie.id}
              className="animate-fade-in-up"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-block px-3 py-1 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap">
                  {movie.subcategory}
                </span>
                <span className="text-white/70 text-sm">{movie.year}</span>
                <span className="text-accent-400 text-sm font-semibold">
                  <i className="ri-star-fill mr-1"></i>
                  {movie.rating}
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                {movie.title}
              </h2>
              <p className="text-sm md:text-base text-white/80 max-w-2xl line-clamp-2 mb-4">
                {movie.description}
              </p>
              <div className="flex items-center gap-3">
                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即观看
                </Link>
                <span className="text-white/60 text-xs">{movie.updateInfo}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 flex items-center gap-2">
        {hotMovies.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300 ${
              idx === currentIndex
                ? 'bg-primary-500 w-6'
                : 'bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`切换到第${idx + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((currentIndex - 1 + hotMovies.length) % hotMovies.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-white text-xl"></i>
      </button>
      <button
        onClick={() => goTo((currentIndex + 1) % hotMovies.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-white text-xl"></i>
      </button>
    </section>
  );
}